import { OnboardingType, JobAPIType } from "../../../Types"
import { MainThunkDispatch, State } from "../../../../Reducers"
import {
    archiveApps,
    createInstance,
    updateInstance
} from "../../AppInstance/AppInstanceActions"
import { failMessage } from "../../../Client/MessagesActions"
import { safePromise } from "../../../../Helpers/Functions/promise"
import { apiEndpoints } from "../../../../Routes/api"
import { apiFetch, pollAPI, killPoll } from "../../../Client/NetworkActions"
import { isValidObject } from "../../../../Helpers/Functions/object"
import {
    createOnboardingJob,
    pollForFirstInProgressJob
} from "../../Job/JobActions"
import {
    APIAppTemplateModel,
    APISampleAppModel,
    nameToSlug,
    TemplateName,
    TemplateSlug
} from "../../../../Models/API/AppTemplateModel"
import { routePaths } from "../../../../Routes"
import { push, replace } from "connected-react-router"
import { openModal } from "../../../Client/ModalActions"
import { ModalViewName } from "../../../../Models/Client/Modal"
import {
    isAuthenticatedOrResumingSession,
    isFullPlanUser,
    selectUserName
} from "../../../../Helpers/Functions/Selectors/auth"
import {
    TemplateInstantiateStage,
    TemplateInstantiation
} from "../../../../Models/Client/TemplateInstantiation"
import { changeInputValueWithValidation } from "../../../Client/InputRender/InputRenderActions"
import { APIAppInstanceModel } from "../../../../Models/API/AppInstanceModel"
import { APIJobModel } from "../../../../Models/API/JobModel"
import { canSubmit } from "../../../../Reducers/Client/InputRender/InputRenderReducer"
import {
    selectLastJobID,
    selectTemplate,
    selectInstance
} from "../../../../Helpers/Functions/Selectors/onboarding"
import { TemplateInstantiateAction } from "../../../../Reducers/API/AppTemplate/Interface/TemplateInstantiateReducer"
import { LabelerConfig } from "../../../../Models/DataProgram/LabelerRestriction"
import { reportError } from "../../../../Helpers/Functions/sentry"
import { getTemplateByName } from "../../Markteplace/MarketplaceAppActions"
import { JobSubmitData } from "../../../../Models/Client/JobSubmitData"

const CODE_POLL = "ONBOARDING_CODE_POLL"
const JOB_POLL = "onboarding_job"
const OUTPUT_POLL = "onboarding_output"
const IN_PROGRESS_JOB_POLL = "onboarding_inprogress_job"

export const startOnboarding =
    (
        orgUsername: string | undefined,
        templateName: TemplateName,
        template?: APIAppTemplateModel,
        params?: any,
        replacePath?: boolean
    ) =>
    async (dispatch: MainThunkDispatch, getState: () => State) => {
        if (template === undefined) {
            template = await dispatch(getTemplateByName(templateName))
        }
        if (template !== undefined) {
            dispatch({
                type: OnboardingType.start,
                payload: { template, orgUsername }
            })

            const onboarding = getState().api.interface.templateInstantiate

            if (onboarding === null) {
                return
            }

            if (params) {
                dispatch(
                    changeInputValueWithValidation(
                        onboarding.paramsRenderId,
                        params,
                        template.appParamsSchema === null ||
                            template.appParamsSchema.params === null
                            ? {}
                            : template.appParamsSchema.params,
                        true
                    )
                )
            }

            const slug = nameToSlug(template.name)

            isAuthenticatedOrResumingSession(getState())
                ? dispatch(nextSetUser(slug, onboarding, replacePath))
                : dispatch(
                      replacePath
                          ? replace(routePaths.onboardingSetUser(slug))
                          : push(routePaths.onboardingSetUser(slug))
                  )
        } else {
            dispatch(failMessage("Could not find template"))
            throw new Error("Could not find template")
        }
    }

const isDead =
    (onboardingId: string, attempt: number) =>
    (dispatch: MainThunkDispatch, getState: () => State) => {
        const currState = getState().api.interface.templateInstantiate

        return (
            currState === null ||
            currState.id !== onboardingId ||
            currState.jobAttempts.length !== attempt
        )
    }
export const onboardingSubmitData =
    () => async (dispatch: MainThunkDispatch, getState: () => State) => {
        /**
         * Get onboarding related state
         */
        const onboarding = getState().api.interface.templateInstantiate

        if (onboarding === null) {
            return
        }
        const isVisualOnboarding =
            getState().client.inputRender.visualOnboarding
        const onboardingId = onboarding.id
        const attempt = onboarding.jobAttempts.length
        const inputForm =
            getState().client.inputRender.entries[onboarding.inputDataRenderId]
        const template = selectTemplate(getState())

        if (
            template === undefined ||
            !canSubmit(inputForm) ||
            onboarding.instanceID === undefined
        ) {
            return
        }

        /**
         * A fresh onboarding code is required if we are going to
         * submit an onboarding job
         */
        const { data: getCodeResponse, error: codeError } = await safePromise(
            dispatch(
                apiFetch(
                    "get_onboarding_code",
                    apiEndpoints.getOnboardingCode({})
                )
            )
        )

        if (dispatch(isDead(onboardingId, attempt))) return

        if (
            codeError ||
            !isValidObject(getCodeResponse) ||
            typeof getCodeResponse.code !== "number"
        ) {
            dispatch(failMessage("Could not create data point", codeError))
            return
        }

        /**
         * Creating the job doesn't immediately resolve any job info, including ID
         * After we create the job we need to use the onboarding code
         * to get the job ID
         */
        const { error: createOnboardingJobError } = await safePromise(
            dispatch(
                createOnboardingJob(
                    onboarding.instanceID,
                    getCodeResponse.code,
                    inputForm.values as JobSubmitData
                )
            )
        )

        if (
            dispatch(isDead(onboardingId, attempt)) ||
            createOnboardingJobError !== undefined
        ) {
            return
        }

        if (
            onboarding.canoticCrowd ||
            onboarding.aiWorkflow ||
            isVisualOnboarding
        ) {
            // if canotic crowd or AI workflow, wait for the job output
            dispatch(pollSingleOnboardingJob(onboarding, getCodeResponse.code))
        } else {
            // otherwise wait for a labeling task
            dispatch(pollInProgressJob(onboarding))
        }
    }

/**
 * This polls for job when one job is added while onboarding
 */
const pollSingleOnboardingJob =
    (onboarding: TemplateInstantiation, onboardingCode: number) =>
    (dispatch: MainThunkDispatch) => {
        function handleCodeResponse(codeResponse: any) {
            if (
                !isValidObject(codeResponse) ||
                typeof codeResponse.uuid !== "string"
            ) {
                return
            }

            dispatch(killPoll(CODE_POLL))

            handleJob(codeResponse.uuid)
        }

        function handleJob(jobID: string) {
            if (dispatch(isDead(onboarding.id, onboarding.jobAttempts.length)))
                return

            dispatch({
                type: OnboardingType.setJobID,
                payload: jobID
            })

            dispatch(
                pollAPI(
                    JOB_POLL,
                    apiEndpoints.getJobByID({
                        jobID
                    }),
                    res => handleJobResponse(jobID, res),
                    undefined,
                    5000
                )
            )
        }

        function handleJobResponse(jobID: string, jobData: any) {
            if (!isValidObject(jobData)) {
                return
            }

            dispatch(killPoll(JOB_POLL))

            if (
                dispatch(
                    isDead(onboarding.id, onboarding.jobAttempts.length)
                ) ||
                onboarding?.instanceID === undefined
            )
                return

            dispatch({
                type: JobAPIType.setOne,
                meta: {
                    appID: onboarding.instanceID,
                    jobID
                },
                payload: jobData
            })

            dispatch(
                pollAPI(
                    OUTPUT_POLL,
                    apiEndpoints.getJobResponseByID({
                        jobID
                    }),
                    res => handleJobOutputResponse(jobData, res),
                    undefined,
                    5000
                )
            )
        }

        function handleJobOutputResponse(
            job: APIJobModel,
            outputResponse: any
        ) {
            if (
                !isValidObject(outputResponse) ||
                outputResponse.response === null
            ) {
                return
            }

            dispatch(killPoll(OUTPUT_POLL))

            if (dispatch(isDead(onboarding.id, onboarding.jobAttempts.length)))
                return

            dispatch({
                type: JobAPIType.setResponse,
                meta: {
                    jobID: job.id
                },
                payload: outputResponse
            })

            dispatch(openModal(ModalViewName.askIfReviewJobOutput))
        }

        dispatch(
            pollAPI(
                CODE_POLL,
                apiEndpoints.checkOnboardingCode({
                    submitCode: onboardingCode
                }),
                handleCodeResponse,
                undefined,
                3000
            )
        )
    }

/**
 * Polls for in progress jobs and looks for one in-progress job
 */
const pollInProgressJob =
    (onboarding: TemplateInstantiation) => (dispatch: MainThunkDispatch) => {
        if (onboarding.instanceID === undefined) {
            return
        }

        function handleJobListResponse(data: any) {
            if (
                !isValidObject(data) ||
                data.total === undefined ||
                data.total === 0
            ) {
                return
            }

            dispatch(killPoll(IN_PROGRESS_JOB_POLL))

            if (dispatch(isDead(onboarding.id, onboarding.jobAttempts.length)))
                return

            dispatch({
                type: OnboardingType.setJobID,
                payload: data.jobs[0].uuid
            })

            if (onboarding.instanceID !== undefined) {
                dispatch({
                    type: JobAPIType.setOne,
                    meta: {
                        appID: onboarding.instanceID,
                        jobID: data.jobs[0].uuid
                    },
                    payload: data.jobs[0]
                })
            }
        }

        // fetch an in progress job as the backend queue processes job in batches, in progress jobs will be processed first
        dispatch(
            pollForFirstInProgressJob(
                IN_PROGRESS_JOB_POLL,
                onboarding.instanceID,
                handleJobListResponse
            )
        )
    }

export const completeOnboarding = () => async (dispatch: MainThunkDispatch) => {
    const endpoint = apiEndpoints.completeOnboarding({})
    const options: RequestInit = {
        method: "POST"
    }

    await dispatch(
        apiFetch("complete_onboarding", endpoint, options, undefined, true)
    )
}

function toggleEmailConstraint(email: string, constraints?: LabelerConfig) {
    const emails: string[] =
        constraints?.taskConstraints?.CANOTIC_OVERRIDE?.emails?.includes(email)
            ? [
                  ...(constraints?.taskConstraints?.CANOTIC_OVERRIDE?.emails ||
                      [])
              ].filter(item => item !== email)
            : [
                  ...(constraints?.taskConstraints?.CANOTIC_OVERRIDE?.emails ||
                      []),
                  email
              ]

    return {
        ...constraints,
        taskConstraints: {
            CANOTIC_OVERRIDE: {
                emails: emails,
                excluded: [],
                priceTag: "EASY"
            }
        }
    }
}

const createOrUpdateInstanceIfNecessary =
    () =>
    async (
        dispatch: MainThunkDispatch,
        getState: () => State
    ): Promise<APIAppInstanceModel | undefined> => {
        const onboarding = getState().api.interface.templateInstantiate

        if (onboarding === null) {
            return Promise.reject(Error("Unexpected state"))
        }

        const paramsForm =
            getState().client.inputRender.entries[onboarding.paramsRenderId]
        if (!onboarding.isAppParamsNull && !canSubmit(paramsForm)) {
            return Promise.reject(Error("Unexpected state"))
        }

        const paramsRequest = onboarding.isAppParamsNull
            ? null
            : { params: paramsForm?.values }
        const { instanceID, templateIdentifier, demo, canoticCrowd } =
            onboarding
        const email = getState().api.authentication.user?.email

        if (instanceID === undefined) {
            let instance = await dispatch(
                createInstance(
                    onboarding.orgUsername,
                    templateIdentifier,
                    paramsRequest,
                    canoticCrowd,
                    true
                )
            )

            if (instance === undefined) {
                return Promise.reject(Error("Unexpected state"))
            }

            if (demo && email) {
                instance = await dispatch(
                    updateInstance(instance.uuid, {
                        constraints: {
                            taskConstraints: {
                                CANOTIC_OVERRIDE: {
                                    emails: [email],
                                    excluded: [],
                                    priceTag: "EASY"
                                }
                            }
                        }
                    })
                )
            }

            dispatch({
                type: OnboardingType.setInstanceID,
                payload: instance.uuid
            })

            return instance
        }

        const currentInstance = selectInstance(getState())

        if (currentInstance === undefined) {
            return Promise.reject(Error("Unexpected state"))
        }

        const updateParams =
            JSON.stringify(currentInstance.appParams) !==
            JSON.stringify(paramsRequest)

        const instanceIsDemo = email
            ? currentInstance.constraints?.taskConstraints?.CANOTIC_OVERRIDE?.emails?.includes(
                  email
              ) ?? false
            : false

        const updateConstraints =
            email && instanceIsDemo !== demo
                ? toggleEmailConstraint(email, currentInstance.constraints)
                : undefined

        if (updateParams || updateConstraints !== undefined) {
            return dispatch(
                updateInstance(currentInstance.uuid, {
                    ...(updateParams ? { appParams: paramsRequest } : {}),
                    ...(updateConstraints
                        ? { constraints: updateConstraints }
                        : {})
                })
            )
        }

        return currentInstance
    }

export const endOnboarding =
    () => (dispatch: MainThunkDispatch, getState: () => State) => {
        dispatch({ type: OnboardingType.clear })
        dispatch(completeOnboarding())
    }

export const endOnboardingGoToReview =
    () => (dispatch: MainThunkDispatch, getState: () => State) => {
        const onboarding = getState().api.interface.templateInstantiate
        const user = getState().api.authentication.user
        const jobID = selectLastJobID(getState())
        if (onboarding === null || user === null || jobID === undefined) {
            return
        }

        const instance = onboarding.instanceID

        if (instance === undefined) {
            return
        }

        dispatch(endOnboarding())
        return dispatch(
            push(routePaths.jobsReview(user.username, instance, jobID))
        )
    }

export const endOnboardingGoToUpload =
    () => (dispatch: MainThunkDispatch, getState: () => State) => {
        const onboarding = getState().api.interface.templateInstantiate
        const user = getState().api.authentication.user
        const jobID = selectLastJobID(getState())
        if (onboarding === null || user === null || jobID === undefined) {
            return
        }

        const instance = onboarding.instanceID

        if (instance === undefined) {
            return
        }

        dispatch(endOnboarding())
        return dispatch(
            push(routePaths.appAddData(user.username, instance, "input"))
        )
    }

export const onboardingForward =
    (currentStage: TemplateInstantiateStage) =>
    async (dispatch: MainThunkDispatch, getState: () => State) => {
        const state = getState()
        const onboarding = state.api.interface.templateInstantiate
        const user = state.api.authentication.user

        if (onboarding === null) {
            return
        }

        const slug = nameToSlug(onboarding.templateIdentifier)

        switch (currentStage) {
            case TemplateInstantiateStage.setUser:
            case TemplateInstantiateStage.loginUser:
                return dispatch(nextSetUser(slug, onboarding))
            case TemplateInstantiateStage.selectLabeler:
                return dispatch(nextSelectLabeler(slug, onboarding))
            case TemplateInstantiateStage.selectProject:
                return dispatch(nextSelectProject(slug, onboarding))
            case TemplateInstantiateStage.createJob:
                return dispatch(push(routePaths.onboardingSetParameters(slug)))
            case TemplateInstantiateStage.setParameters:
                return dispatch(nextSetParameters(slug, onboarding))
            case TemplateInstantiateStage.prepareLabeling:
                if (onboarding.instanceID === undefined || user === null) {
                    return
                }
                return dispatch(
                    push(
                        routePaths.appDetail(
                            user.username,
                            onboarding.instanceID
                        )
                    )
                )
        }
    }

const nextSetUser =
    (
        slug: TemplateSlug,
        onboarding: TemplateInstantiation,
        replacePath?: boolean
    ) =>
    async (dispatch: MainThunkDispatch, getState: () => State) => {
        const isFullUser = isFullPlanUser(getState())
        const sampleAppsAvailable = !!onboarding.sampleApps
        if (isFullUser) {
            dispatch(setOnboardingLabeler(!!onboarding.aiWorkflow))
        }

        const route = replacePath ? replace : push

        return isFullUser && !onboarding.aiWorkflow
            ? dispatch(route(routePaths.onboardingSetLabeler(slug)))
            : sampleAppsAvailable
            ? dispatch(route(routePaths.onboardingSelectProject(slug)))
            : dispatch(
                  route(
                      routePaths.onboardingCreateJob(
                          slug,
                          onboarding.aiWorkflow ? "json" : "input"
                      )
                  )
              )
    }

const nextSelectLabeler =
    (slug: TemplateSlug, onboarding: TemplateInstantiation) =>
    async (dispatch: MainThunkDispatch) => {
        const sampleAppsAvailable = !!onboarding.sampleApps

        return sampleAppsAvailable
            ? dispatch(push(routePaths.onboardingSelectProject(slug)))
            : dispatch(
                  push(
                      routePaths.onboardingCreateJob(
                          slug,
                          onboarding.aiWorkflow ? "json" : "input"
                      )
                  )
              )
    }

const nextSelectProject =
    (slug: TemplateSlug, onboarding: TemplateInstantiation) =>
    async (dispatch: MainThunkDispatch) => {
        const selectedSampleAppIdentifier =
            onboarding.selectedSampleAppIdentifier

        return selectedSampleAppIdentifier
            ? dispatch(push(routePaths.onboardingSetParameters(slug)))
            : dispatch(
                  push(
                      routePaths.onboardingCreateJob(
                          slug,
                          onboarding.aiWorkflow ? "json" : "input"
                      )
                  )
              )
    }

const nextSetParameters =
    (slug: TemplateSlug, onboarding: TemplateInstantiation) =>
    async (dispatch: MainThunkDispatch, getState: () => State) => {
        try {
            await dispatch(createOrUpdateInstanceIfNecessary())
        } catch (e) {
            reportError(e)
            return
        }
        dispatch(onboardingSubmitData()).catch(e => {
            reportError(e)
            dispatch(push(routePaths.onboardingSetParameters(slug)))
        })
        const state = getState()
        const user = state.api.authentication.user
        const instanceID = state.api.interface.templateInstantiate?.instanceID
        const isVisualOnboarding = state.client.inputRender.visualOnboarding
        if (instanceID === undefined || user === null) {
            return
        }
        return onboarding.canoticCrowd ||
            onboarding.aiWorkflow ||
            isVisualOnboarding
            ? dispatch(push(routePaths.appDetail(user.username, instanceID)))
            : dispatch(push(routePaths.onboardingPrepareLabeling(slug)))
    }

export const onboardingBack =
    (currentStage: TemplateInstantiateStage) =>
    async (dispatch: MainThunkDispatch, getState: () => State) => {
        const onboarding = getState().api.interface.templateInstantiate

        if (onboarding === null) {
            return
        }

        const slug = nameToSlug(onboarding.templateIdentifier)
        const isSampleAppSelected =
            onboarding.selectedSampleAppIdentifier !== undefined
        const sampleAppsAvailable = !!onboarding.sampleApps
        const createJobStep = routePaths.onboardingCreateJob(
            slug,
            onboarding.aiWorkflow ? "json" : "input"
        )

        switch (currentStage) {
            case TemplateInstantiateStage.createJob: {
                const backTo = sampleAppsAvailable
                    ? routePaths.onboardingSelectProject(slug)
                    : createJobStep

                return dispatch(push(backTo))
            }
            case TemplateInstantiateStage.setParameters: {
                const backTo = isSampleAppSelected
                    ? routePaths.onboardingSelectProject(slug)
                    : createJobStep

                return dispatch(push(backTo))
            }

            case TemplateInstantiateStage.prepareLabeling:
                dispatch(resetOnboarding())
                return dispatch(push(routePaths.onboardingSetParameters(slug)))
        }
    }

export const onboardingBackToInput =
    () => async (dispatch: MainThunkDispatch, getState: () => State) => {
        const onboarding = getState().api.interface.templateInstantiate

        if (onboarding === null) {
            return
        }

        const slug = nameToSlug(onboarding.templateIdentifier)
        const sampleAppsAvailable = !!onboarding.sampleApps
        dispatch(resetOnboarding())

        return sampleAppsAvailable
            ? dispatch(push(routePaths.onboardingSelectProject(slug)))
            : dispatch(
                  push(
                      routePaths.onboardingCreateJob(
                          slug,
                          onboarding.aiWorkflow ? "json" : "input"
                      )
                  )
              )
    }

export const onboardingBackToParameters =
    () => async (dispatch: MainThunkDispatch, getState: () => State) => {
        const onboarding = getState().api.interface.templateInstantiate

        if (onboarding === null) {
            return
        }

        const slug = nameToSlug(onboarding.templateIdentifier)

        dispatch(resetOnboarding())

        return dispatch(push(routePaths.onboardingSetParameters(slug)))
    }

export const onboardingBackToLabelerSelect =
    () => async (dispatch: MainThunkDispatch, getState: () => State) => {
        const onboarding = getState().api.interface.templateInstantiate

        if (onboarding === null) {
            return
        }

        const slug = nameToSlug(onboarding.templateIdentifier)

        dispatch(resetOnboarding())

        return dispatch(push(routePaths.onboardingSetLabeler(slug)))
    }

export const onboardingBackToChooseOrganization =
    () => async (dispatch: MainThunkDispatch, getState: () => State) => {
        const onboarding = getState().api.interface.templateInstantiate

        if (onboarding === null) {
            return
        }

        const slug = nameToSlug(onboarding.templateIdentifier)

        dispatch(resetOnboarding())

        return dispatch(push(routePaths.onboardingChooseOrganization(slug)))
    }

export const killOnboardingPolls = () => (dispatch: MainThunkDispatch) => {
    dispatch(killPoll(CODE_POLL))
    dispatch(killPoll(JOB_POLL))
    dispatch(killPoll(OUTPUT_POLL))
    dispatch(killPoll(IN_PROGRESS_JOB_POLL))
}

// archive the app and reset onboarding (job attempts and instance)
export const resetOnboarding =
    () => (dispatch: MainThunkDispatch, getState: () => State) => {
        dispatch(killOnboardingPolls())
        const onboarding = getState().api.interface.templateInstantiate
        const orgUsername =
            onboarding?.orgUsername ?? selectUserName(getState())

        if (onboarding?.instanceID === undefined || orgUsername === undefined) {
            return
        }

        const { instanceID } = onboarding

        dispatch(archiveApps(orgUsername, [instanceID], true)).catch(() => {
            // do nothing
        })
        dispatch({
            type: OnboardingType.resetAttempt
        })
        dispatch({ type: OnboardingType.addAttempt })
    }

export const toggleDemoMode = () => {
    const action: TemplateInstantiateAction = {
        type: OnboardingType.toggleDemo
    }

    return action
}

export const setOnboardingLabeler = (canoticCrowd: boolean) => {
    const action: TemplateInstantiateAction = {
        type: OnboardingType.setLabeler,
        payload: canoticCrowd
    }

    return action
}

export const setSampleAppIdentifier = (
    selectedSampleAppIdentifier?: string
) => {
    const action: TemplateInstantiateAction = {
        type: OnboardingType.setSampleAppIdentifier,
        payload: selectedSampleAppIdentifier
    }

    return action
}

export const getSampleApp =
    (workflow: TemplateName, sampleAppIdentifier: string) =>
    async (
        dispatch: MainThunkDispatch
    ): Promise<APISampleAppModel | undefined> => {
        const endpoint = apiEndpoints.getSampleApp({
            workflow,
            sampleAppIdentifier
        })
        const { data } = await safePromise(
            dispatch(
                apiFetch<APISampleAppModel>(
                    `GET_SAMPLE_APP_${workflow}_${sampleAppIdentifier}`,
                    endpoint
                )
            )
        )

        if (data !== undefined) {
            dispatch({
                type: OnboardingType.setSampleAppData,
                meta: {
                    sampleAppIdentifier
                },
                payload: data
            })
        }
        return data
    }
